import React, { useEffect, useId, useRef, useState } from "react";
import styled from "@emotion/styled";
import CalendarDateInput from "../../common/dashboard/CalendarDateInput";
import { MultiOptionGroup as SharedMultiOptionGroup } from "../../common/dashboard/FormFields";
import type {
  FormCalendarDateFieldProps,
  MultiOptionGroupProps as SharedMultiOptionGroupProps,
} from "../../common/dashboard/FormFields";
import { StepProgress } from "../../common/dashboard/StepProgress";
import { DashboardLogoUploadField } from "../../common/dashboard/LogoUploadField";
import {
  ChamberMemberCategory,
  IdaEntityType,
  IdaGenerateMode,
  useIdaGenerateMutation,
} from "../../../graphql/types";
import { getPreserveExpectedErrorsForCallerContext } from "../../../utils/apolloErrorPolicy";

export const memberRequestApolloContext =
  getPreserveExpectedErrorsForCallerContext({ expectedGraphQLErrorCodes: [] });

// The shared form primitives that used to live in this file are promoted to
// src/components/common/dashboard/ (Phase A of the platform polish pass).
// This module re-exports them under their original names so the member
// workflow pages (and Events Step1EventDetails) compile unchanged, and keeps
// only the member-module-specific pieces: workflow steps and their display
// resolvers, navigation-state helpers, and thin member-flavored wrappers.
export {
  cpaGold,
  cpaGoldDark,
  fieldSx,
  choiceControlLabelSx,
  checkboxControlSx,
  radioControlSx,
  selectionChipSx as memberSelectionChipSx,
} from "../../common/dashboard/formStyles";
export { SectionShell } from "../../common/dashboard/SectionShell";
export type { SectionShellProps } from "../../common/dashboard/SectionShell";
export {
  FormTextField,
  FormCountryField,
  FormPhoneField,
  FormSelectField,
  FormMultiSelectField,
} from "../../common/dashboard/FormFields";
export type {
  FormTextFieldProps,
  FormCountryFieldProps,
  FormPhoneFieldProps,
  FormCalendarDateFieldProps,
  FormSelectFieldProps,
  FormMultiSelectFieldProps,
} from "../../common/dashboard/FormFields";
export { getLogoInputValue } from "../../common/dashboard/LogoUploadField";

export const getMemberEstablishedYearOptions = (currentValue: string) =>
  Array.from(
    new Set([
      currentValue,
      ...Array.from({ length: 80 }, (_, index) =>
        String(new Date().getFullYear() - index),
      ),
    ]),
  )
    .filter(Boolean)
    .map((year) => ({ value: year, label: year }));

export type MultiOptionGroupProps = SharedMultiOptionGroupProps & {
  baselineSelected?: readonly string[];
};

const MemberOptionGroupSurface = styled.div<{ changedIndices: number[]; inlineOptions: boolean }>(
  ({ changedIndices, inlineOptions }) => ({
    marginBottom: 20,
    "& > .mb-5": { marginBottom: 0 },
    ...(inlineOptions ? {
      "@media (min-width: 1280px)": {
        "& > .mb-5 > .grid": { display: "flex", flexWrap: "wrap", gap: 26 },
      },
    } : {}),
    ...Object.fromEntries(
      changedIndices.map((index) => [
        `& .MuiFormControlLabel-root:nth-of-type(${index + 1}) .MuiCheckbox-root`,
        { outline: "2px solid #E1A731", outlineOffset: -1 },
      ]),
    ),
    "&[data-member-field-error='true'] .MuiCheckbox-root": {
      outline: "none !important",
      color: "#E24C4C",
    },
    "&[data-member-field-error='true'] .MuiCheckbox-root::before": {
      borderColor: "#E24C4C",
    },
  }),
);

export const MultiOptionGroup = ({
  baselineSelected,
  error,
  ...props
}: MultiOptionGroupProps) => {
  const groupId = useId();
  const requiredDescriptionId = `${groupId}-required`;
  const errorId = `${groupId}-error`;
  const descriptionIds = [
    props.required ? requiredDescriptionId : "",
    error ? errorId : "",
  ].filter(Boolean).join(" ");
  const changedIndices = baselineSelected === undefined ? [] :
    props.options.flatMap((option, index) =>
      props.selected.includes(option) !== baselineSelected.includes(option)
        ? [index]
        : [],
    );
  return (
    <MemberOptionGroupSurface
      changedIndices={changedIndices}
      inlineOptions={props.columns === "inline"}
      role="group"
      aria-label={props.label}
      aria-describedby={descriptionIds || undefined}
      data-member-field-error={error ? "true" : undefined}
    >
      {props.required && (
        <span id={requiredDescriptionId} className="sr-only">
          Select at least one option.
        </span>
      )}
      <SharedMultiOptionGroup {...props} />
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1 text-xs leading-[1.55] text-[#E24C4C]"
        >
          {error}
        </p>
      )}
    </MemberOptionGroupSurface>
  );
};

export const FormCalendarDateField = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  error,
  required,
  disabled,
  description,
  iconPosition = "right",
}: FormCalendarDateFieldProps) => (
  <div
    className="min-w-0 cmp-dashboard-density-form-field"
    data-member-field-error={Boolean(error)}
  >
    <label
      htmlFor={id}
      className="mb-1.5 block text-sm font-bold leading-5 text-[#0A0A0A]"
    >
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    {description && (
      <div className="mb-1.5 text-xs leading-[1.5] text-[#727272]">
        {description}
      </div>
    )}
    <CalendarDateInput
      id={id}
      ariaLabel={label}
      mode="fullDate"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      appearance="claude"
      yearRange="historical"
      displayFormat="long"
      iconPosition={iconPosition}
    />
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
);

export const updateMemberNavigationStorageKey = "cmp-update-member-id";

const updateMemberNavigationStorageMaxAgeMs = 60 * 60 * 1000;

export type UpdateMemberNavigationState = {
  memberId?: string;
};

export type MemberWorkflowStep = {
  title: string;
  description: string;
};

export const memberWorkflowSteps: MemberWorkflowStep[] = [
  { title: "Member Information", description: "Directory Details Only" },
  { title: "Member Contacts", description: "Contact Details" },
  { title: "Member Insights", description: "Member Details" },
  { title: "Membership Type & Billing", description: "Payment Details" },
];

export const updateMemberWorkflowSteps: MemberWorkflowStep[] = [
  { title: "Member Information", description: "Directory Details Only" },
  { title: "Member Contacts", description: "Contact Details" },
  { title: "Member Insights", description: "Member Details" },
  { title: "Membership Details", description: "View Only" },
  { title: "Membership Renewal", description: "Review & Update Details" },
];

// Keep the existing field owners. This presentation scope changes only the
// Members pages that opt in and does not affect their other consumers.
export const MemberDesignSurface = styled.div({
  color: "#0A0A0A",
  "& .text-sm": { fontSize: 14 },
  "& .text-xs": { fontSize: 12 },
  "& .rounded-md": { borderRadius: 6 },
  "& .rounded-lg": { borderRadius: 8 },
  "& .rounded-xl": { borderRadius: 12 },
  "& .gap-4": { gap: 16 },
  "& .gap-5": { gap: 20 },
  "& .gap-6": { gap: 24 },
  "& .gap-7": { gap: 28 },
  "& .gap-x-4": { columnGap: 16 },
  "& .gap-x-5": { columnGap: 20 },
  "& .gap-y-4": { rowGap: 16 },
  "& .gap-y-5": { rowGap: 20 },
  "& .space-y-4 > :not([hidden]) ~ :not([hidden])": { marginTop: 16 },
  "& .space-y-5 > :not([hidden]) ~ :not([hidden])": { marginTop: 20 },
  "& .space-y-6 > :not([hidden]) ~ :not([hidden])": { marginTop: 24 },
  "& .mt-1\\.5": { marginTop: 6 },
  "& .mb-1\\.5": { marginBottom: 6 },
  "& .mb-6": { marginBottom: 24 },
  "& .px-3\\.5": { paddingLeft: 14, paddingRight: 14 },
  "& .px-7": { paddingLeft: 28, paddingRight: 28 },
  "& .py-2": { paddingTop: 8, paddingBottom: 8 },
  "& .py-3": { paddingTop: 12, paddingBottom: 12 },
  "&.member-create-page > .min-h-full > .mt-8": {
    marginTop: 24,
    borderTop: 0,
    padding: "0 0 8px",
  },
  "&.member-create-page > .min-h-full > .mt-8 > div": {
    maxWidth: "none",
    padding: 0,
  },
  "&.member-create-page > .min-h-full > .mt-8 > div > .flex": { gap: 16 },
  "&.member-create-page > .min-h-full > .mt-8 .MuiButton-startIcon": {
    marginLeft: 0,
    marginRight: 10,
  },
  "&.member-create-page > .min-h-full > .mt-8 .MuiButton-endIcon": {
    marginLeft: 10,
    marginRight: 0,
  },
  "&.member-create-page > .min-h-full > .mt-8 .MuiButton-root": {
    minWidth: 0,
    fontSize: 13,
    padding: "12px 18px",
  },
  "&.member-create-page > .min-h-full > .mt-8 .MuiButton-contained": {
    padding: "13px 22px",
    fontWeight: 700,
  },
  "& .cmp-dashboard-density-form-field": { marginBottom: 0 },
  "& .cmp-dashboard-density-form-field .MuiInputBase-input": { fontSize: 14 },
  "& .cmp-dashboard-density-form-field > label": {
    fontSize: "14px !important",
    marginBottom: 6,
    lineHeight: "20px",
  },
  fontFamily: '"DM Sans", sans-serif',
  "& .MuiInputBase-root": {
    fontFamily: "inherit",
    fontSize: 14,
    color: "#0A0A0A",
  },
  "& .MuiOutlinedInput-root": { borderRadius: 8, backgroundColor: "#FFFFFF" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#9E9E9E" },
  "& .MuiOutlinedInput-root.Mui-disabled": { backgroundColor: "#F7F7F8" },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ECECEC",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E1A731",
    borderWidth: 1,
  },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E24C4C",
  },
  "& .MuiOutlinedInput-input": {
    padding: "11px 12px",
    fontSize: 14,
    lineHeight: "20px",
  },
  "& .MuiInputBase-multiline": { padding: 0 },
  "& textarea.MuiOutlinedInput-input": { fontSize: 14, lineHeight: "normal" },
  "& textarea.MuiOutlinedInput-input:not([aria-hidden='true'])": {
    boxSizing: "border-box",
    minHeight: 76,
    resize: "vertical",
  },
  "& textarea.MuiOutlinedInput-input:not([aria-hidden='true'])[rows='4']": {
    minHeight: 96,
  },
  "& textarea.MuiOutlinedInput-input[aria-hidden='true']": {
    minHeight: 0,
    resize: "none",
  },
  "& .MuiInputBase-input::placeholder": { color: "#9E9E9E", opacity: 1 },
  "& label .text-red-500": { color: "#F0202E" },
  "& .MuiFormControlLabel-root": {
    margin: 0,
    gap: 10,
    alignItems: "flex-start",
  },
  "& .MuiFormControlLabel-root .MuiFormControlLabel-label": {
    fontSize: 14,
    fontFamily: "inherit",
    paddingTop: 0,
    lineHeight: 1.5,
  },
  "& .MuiCheckbox-root, & .MuiRadio-root": {
    padding: 0,
    width: 18,
    height: 18,
    marginTop: 2,
  },
  // The checkbox and radio look comes from cmpTheme (Monday 13396402468).
  "& button[id][aria-haspopup='dialog']": {
    height: "auto",
    minHeight: 44,
    paddingTop: 11,
    paddingBottom: 11,
    borderColor: "#ECECEC",
    borderRadius: 8,
    fontSize: 14,
    lineHeight: "20px",
  },
  "& .MuiButton-root": {
    fontFamily: "inherit",
    borderRadius: 8,
    padding: "11px 20px",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "20px",
    textTransform: "none",
    boxShadow: "none",
  },
  "&.member-create-page .member-affinity-save": { padding: "11px 34px" },
  "& .member-field-dirty .MuiOutlinedInput-root, & .member-field-dirty input:not([type='checkbox']):not([type='radio']), & .member-field-dirty textarea, & .member-field-dirty select, & [data-dirty='true'] .MuiOutlinedInput-root, & [data-dirty='true'] input:not([type='checkbox']):not([type='radio']), & [data-dirty='true'] textarea, & [data-dirty='true'] select":
    { outline: "2px solid #E1A731", outlineOffset: -1 },
  "& .member-field-dirty [data-member-select-trigger], & .member-field-dirty button[aria-haspopup='dialog'], & [data-dirty='true'] [data-member-select-trigger], & [data-dirty='true'] button[aria-haspopup='dialog']":
    { outline: "2px solid #E1A731", outlineOffset: -1 },
  "& .MuiOutlinedInput-root.Mui-error, & [aria-invalid='true'], & [data-member-field-error='true'], & [data-member-field-error='true'] input, & [data-member-field-error='true'] textarea, & [data-member-field-error='true'] [aria-haspopup='dialog']":
    { outline: "none !important" },
  "& [data-member-field-error='true'] input[aria-haspopup='dialog'], & [data-member-field-error='true'] button[aria-haspopup='dialog']":
    { borderColor: "#E24C4C !important" },
  // CMP Dashboard (19): red border on a contact name or email that must be
  // filled before dashboard access can be granted.
  "& .member-field-access-missing .MuiOutlinedInput-notchedOutline": { borderColor: "#E24C4C !important" },
  "& .member-field-access-missing .MuiOutlinedInput-root, & .member-field-access-missing input": { outline: "none !important" },
});

const MemberStepperSurface = styled.div<{
  update: boolean;
  desktopLayout: "grid" | "content";
}>(({ update, desktopLayout }) => ({
  display: "contents",
  "& nav": {
    borderColor: "#ECECEC",
    margin: 0,
    maxWidth: "none",
    width: "100%",
  },
  "& nav ol": { padding: 0, gap: 8 },
  "& nav li": { padding: "6px 10px 14px", gap: 8 },
  ...(update ? { "& nav li > button": { gap: 10 } } : {}),
  "& nav li > .absolute": { height: 3, bottom: -1 },
  "& nav li .rounded-full": {
    width: 32,
    height: 32,
    fontSize: 14,
    fontWeight: 700,
  },
  "& nav li:not([aria-current='step']) .rounded-full": {
    backgroundColor: "#ECECEC",
    color: "#0A0A0A",
  },
  "& nav li span.min-w-0.flex-1 > span:first-of-type": {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: update ? "normal" : "18px",
  },
  "& nav li span.min-w-0.flex-1 > span:nth-of-type(2)": {
    fontSize: 12,
    color: update ? "#000000" : "#727272",
    lineHeight: update ? "normal" : "17px",
    ...(update ? { marginTop: 1 } : {}),
  },
  "& nav li .MuiSvgIcon-root": { width: 16, height: 16, color: "#9E9E9E" },
  "& nav li .MuiSvgIcon-root.text-cpaGold": { color: "#E1A731" },
  ...(desktopLayout === "content" ? {
    "@media (min-width: 1024px)": {
      "& nav ol": { display: "flex", flexWrap: "wrap", alignItems: "stretch" },
      "& nav li": {
        flex: "0 0 auto",
        maxWidth: "100%",
        padding: 0,
        marginBottom: -1,
      },
      "& nav li > div": {
        flex: "0 1 auto",
        padding: "6px 10px 14px",
        gap: 10,
        borderBottom: "3px solid transparent",
      },
      "& nav li > span:not(.absolute)": {
        width: 32,
        padding: "0 8px 8px",
        alignSelf: "stretch",
        marginBottom: 1,
      },
      // The shared item contains its leading chevron. Keep that strip outside
      // the Create step underline without changing the shared stepper markup.
      "& nav li > .absolute": { left: 40, width: "calc(100% - 40px)", bottom: 0 },
      "& nav li:first-of-type > .absolute": { left: 0, width: "100%" },
      "& nav li span.min-w-0.flex-1": { flex: "0 1 auto" },
      "& nav li span.min-w-0.flex-1 > span:first-of-type": { lineHeight: "normal" },
      "& nav li span.min-w-0.flex-1 > span:nth-of-type(2)": {
        lineHeight: "normal",
        marginTop: 1,
      },
    },
  } : {}),
}));

export const getUpdateMemberNavigationState = (
  memberId: string,
): UpdateMemberNavigationState => ({
  memberId,
});

export const getUpdateMemberNavigationMemberId = (state: unknown): string => {
  if (!state || typeof state !== "object") {
    return "";
  }

  const memberId = (state as { memberId?: unknown }).memberId;

  return typeof memberId === "string" ? memberId : "";
};

export const storeUpdateMemberNavigationMemberId = (
  memberId?: string | null,
) => {
  if (!memberId || typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    updateMemberNavigationStorageKey,
    JSON.stringify({
      memberId,
      storedAtUtcMilli: Date.now(),
    }),
  );
};

export const consumeUpdateMemberNavigationMemberId = (): string => {
  if (typeof window === "undefined") {
    return "";
  }

  const storedValue = window.sessionStorage.getItem(
    updateMemberNavigationStorageKey,
  );

  if (!storedValue) {
    return "";
  }

  window.sessionStorage.removeItem(updateMemberNavigationStorageKey);

  try {
    const parsedValue = JSON.parse(storedValue) as {
      memberId?: unknown;
      storedAtUtcMilli?: unknown;
    };
    const memberId =
      typeof parsedValue.memberId === "string" ? parsedValue.memberId : "";
    const storedAtUtcMilli =
      typeof parsedValue.storedAtUtcMilli === "number"
        ? parsedValue.storedAtUtcMilli
        : 0;

    if (
      !memberId ||
      !storedAtUtcMilli ||
      Date.now() - storedAtUtcMilli > updateMemberNavigationStorageMaxAgeMs
    ) {
      return "";
    }

    return memberId;
  } catch (error) {
    return "";
  }
};

export type MemberStepProgressProps = {
  activeStep: number;
  ariaLabel?: string;
  onStepClick?: (step: number) => void;
  steps?: MemberWorkflowStep[];
  desktopGridColsClassName?: string;
  desktopLayout?: "grid" | "content";
};

export const MemberStepProgress = ({
  activeStep,
  ariaLabel = "Member steps",
  onStepClick,
  steps = memberWorkflowSteps,
  desktopGridColsClassName = "lg:grid-cols-[1.2fr_1fr_1fr_1.2fr]",
  desktopLayout = "grid",
}: MemberStepProgressProps) => (
  <MemberStepperSurface update={steps.length === 5} desktopLayout={desktopLayout}>
    <StepProgress
      activeStep={activeStep}
      ariaLabel={ariaLabel}
      onStepClick={onStepClick}
      steps={steps}
      desktopGridColsClassName={desktopGridColsClassName}
      stepTestIdPrefix="member-step"
    />
  </MemberStepperSurface>
);

export type LogoUploadFieldProps = {
  id: string;
  fileName: string;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  onRemove?: () => void;
  previewAlt?: string;
  previewUrl?: string;
};

// Spread order is load-bearing only if LogoUploadFieldProps ever grows a
// `heading` key: today the spread after `heading` cannot override it because
// the member props type has no such key. Keep `heading` first regardless.
export const LogoUploadField = (props: LogoUploadFieldProps) => (
  <DashboardLogoUploadField
    heading="Member Logo"
    {...props}
    appearance="design"
    confirmationAppearance="design"
  />
);

export type IdaGenerateControlProps = {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
};

export const IdaGenerateControl = ({
  onClick,
  loading = false,
  disabled = false,
}: IdaGenerateControlProps) => (
  <span className="inline-flex items-center gap-[8px]">
    <span
      className="relative block h-[34px] shrink-0 overflow-hidden"
      style={{ width: (34 * 390) / 286 }}
    >
      {/* The existing PNG has a 390 x 286 artwork area at (55, 124). */}
      <img
        src="/ida-chatbot-icon.png"
        alt="IDA"
        className="absolute max-w-none"
        style={{
          width: (34 * 500) / 286,
          height: (34 * 500) / 286,
          left: -(34 * 55) / 286,
          top: -(34 * 124) / 286,
        }}
      />
    </span>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className="inline-flex items-center justify-center gap-[6px] rounded-[6px] bg-[#10B981] px-[14px] py-[8px] text-[12px] font-bold text-white hover:bg-[#0EA271] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {loading && (
        <span
          className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"
          aria-hidden="true"
        />
      )}
      {loading ? "Generating..." : "Generate"}
    </button>
  </span>
);

export const useMemberIdaGeneration = ({
  name,
  description: currentDescription,
  websiteUrl,
  disabled = false,
  onCategories,
}: {
  name: string;
  description: string;
  websiteUrl: string;
  disabled?: boolean;
  onCategories: (ids: ChamberMemberCategory[]) => void;
}) => {
  const [idaGenerate] = useIdaGenerateMutation({
    context: memberRequestApolloContext,
  });
  const [mode, setMode] = useState<IdaGenerateMode | null>(null);
  const [description, setDescription] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState("");
  const [categoryFeedback, setCategoryFeedback] = useState("");
  const inFlight = useRef(false);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    },
    [],
  );
  const generate = async (nextMode: IdaGenerateMode) => {
    if (inFlight.current || disabled) return;
    inFlight.current = true;
    setMode(nextMode);
    setError("");
    setCategoryFeedback("");
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    if (nextMode === IdaGenerateMode.CompanyDescription) setDialogOpen(true);
    try {
      const result = await idaGenerate({
        variables: {
          input: {
            entityName: name.trim() || "the member",
            entityType: IdaEntityType.Company,
            mode: nextMode,
            keyDetails: currentDescription,
            websiteUrl,
            maxLength: 2000,
          },
        },
      });
      const generated = result.data?.idaGenerate;
      if (!generated?.success) throw new Error("Generation failed");
      if (nextMode === IdaGenerateMode.DirectoryCategories) {
        const ids = (generated.suggestedCategories || [])
          .map((category) => category.id)
          .slice(0, 2);
        if (!ids.length)
          setCategoryFeedback("IDA could not find matching categories. Select up to 2 categories from the list.");
        else {
          onCategories(ids);
          setCategoryFeedback(`IDA selected ${ids.length} categories.`);
          feedbackTimer.current = setTimeout(
            () => setCategoryFeedback(""),
            2500,
          );
        }
      } else setDescription(generated.generatedText.slice(0, 2000));
    } catch {
      if (nextMode === IdaGenerateMode.DirectoryCategories)
        setCategoryFeedback("IDA could not suggest categories right now. Select up to 2 categories from the list.");
      else setError("IDA could not generate a description. Try again.");
    } finally {
      inFlight.current = false;
      setMode(null);
    }
  };
  return {
    mode,
    description,
    setDescription,
    dialogOpen,
    setDialogOpen,
    error,
    categoryFeedback,
    generate,
  };
};
